import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./Calendar.css";
import CalendarHeader from "./CalendarHeader";
import TimeSlots from "./TimeSlots";
import { AppointmentData, TimeSlot } from "@/data/interfaces/Appointments";

type CalendarProps = {
  appointmentOptions: AppointmentData;
  onSelect?: (date: string, time?: TimeSlot) => void;
  fetchSlots: (date: string) => Promise<TimeSlot[]>;
};

const formatDate = (d: Date) => d.toISOString().split("T")[0];
const firstMonthDay = new Date(
  new Date().getFullYear(),
  new Date().getMonth(),
  1
);

export default function Calendar({
  appointmentOptions,
  onSelect,
  fetchSlots,
}: CalendarProps) {
  const [activeSelection, setActiveSelection] = useState<{
    date: Date;
    time: TimeSlot;
  }>();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<TimeSlot | null>(null);
  const [times, setTimes] = useState<TimeSlot[] | null>(null);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [timesError, setTimesError] = useState<string | null>(null);

  const resetTimes = () => {
    setSelectedTime(null);
    setTimes(null);
    setTimesError(null);
  };

  useEffect(() => {
    if (selectedDate) loadDateTimes(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    if (appointmentOptions?.dates.length > 0)
      setSelectedDate(new Date(appointmentOptions.dates[0]));
  }, [appointmentOptions?.dates]);

  const loadDateTimes = async (date: Date) => {
    resetTimes();
    setLoadingTimes(true);
    try {
      const times = await fetchSlots(formatDate(date));

      setTimes(times);
      if (!activeSelection?.date || !selectedDate) return;

      if (formatDate(activeSelection?.date) === formatDate(selectedDate)) {
        setSelectedTime(
          times.find((x) => x.value === activeSelection.time.value) ?? null
        );
      }
    } catch (e: any) {
      setTimes([]);
      setTimesError(e?.message || "Nepavyko gauti laikų");
    } finally {
      setLoadingTimes(false);
    }
  };

  const handleTimeClick = (t: TimeSlot) => {
    setSelectedTime(t);
    setActiveSelection({ date: selectedDate!, time: t });
    if (selectedDate && onSelect) onSelect(formatDate(selectedDate), t);
  };

  return (
    <div className="flex flex-col items-stretch w-full bg-[#F3F2F5] rounded-2xl border border-stroke p-4">
      <DatePicker
        inline
        selected={selectedDate}
        onChange={setSelectedDate}
        calendarStartDay={1}
        disabledKeyboardNavigation={true}
        renderCustomHeader={(p) => <CalendarHeader {...p} />}
        calendarClassName={"w-full !bg-transparent !border-0"}
        formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 3).toUpperCase()}
        showPopperArrow={false}
        minDate={firstMonthDay}
        includeDates={appointmentOptions?.dates?.map((x) => new Date(x))}
      />

      <TimeSlots
        loading={loadingTimes}
        error={timesError}
        selectedDate={selectedDate}
        times={times}
        selectedTime={selectedTime}
        onSelectTime={handleTimeClick}
      />
    </div>
  );
}
